/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { AssetsService, EventsService, FormHelpersService, UtilsService } from 'app/shared/services';
import { CustomTypeAheadComponent } from 'app/shared/components/custom-type-ahead/custom-type-ahead.component';
import { Constants } from 'app/shared/models/constants.model';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BaseComponent } from '@core/abstract/base.component';

@Component({
  selector: '[hr-game-select-form]',
  templateUrl: './hr-game-select-form.component.html',
  styleUrls: ['./hr-game-select-form.component.scss'],
})
export class HrGameSelectFormComponent extends BaseComponent implements OnInit {
  @Input() userEvents: any = [];
  @Input() playInfoForm: FormGroup;
  @Input() playInfo: any = {};
  @Input() readonly: boolean;
  @Input() selectClips = false;
  @Input() uploadClip = false;
  @Input() userClips;
  @Input() searchEvents = false;
  @Input() eventOptions: any;
  @Output() uploadClipChange: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('eventAutocomplete') eventAutocomplete: CustomTypeAheadComponent;
  @ViewChild('gameAutocomplete') gameAutocomplete: CustomTypeAheadComponent;
  @ViewChild('clipAutocomplete') clipAutocomplete: CustomTypeAheadComponent;

  selectedTeams: any[];
  gameInfo: any;
  showForm = false;
  eventAutocompleteOptions: any;
  gameAutocompleteOptions: any;
  clipAutocompleteOptions: any;
  dropdownEvents = [];
  dropdownGames = [];
  addClipOptions = [
    { value: 1, name: 'Select an existing clip' },
    { value: 2, name: 'Upload a clip' },
  ];
  eventLoading = false;
  eventInput$ = new Subject<string>();
  eventsList$: Observable<any>;
  gamesLoading: boolean;

  constructor(
    public formHelpersService: FormHelpersService,
    private assetsService: AssetsService,
    private eventsService: EventsService,
    private utilsService: UtilsService
  ) {
    super();
  }

  ngOnInit() {
    this.gameInfo = this.playInfo.gameInfo;
    this.dropdownEvents = this.userEvents
      .map((ev) => ({ ...ev, value: ev.id || null }))
      .sort(this.utilsService.sortStringByProp);

    this.selectedTeams = this.userEvents.reduce((acc, event) => {
      const existingEventIndex = acc.findIndex((existingEvent) => existingEvent.eventId === event.id);

      if (existingEventIndex === -1) {
        acc.push({
          eventId: event.id,
          teams: event.teams.filter((selectedTeam) => selectedTeam),
        });
      } else {
        acc[existingEventIndex].teams = [
          ...acc[existingEventIndex].teams,
          event.teams.filter((selectedTeam) => selectedTeam),
        ];
      }

      return acc;
    }, []);

    this.setGamesDropdown();
    this.loadEvents();

    this.showForm = true;
  }

  formatGamesResponse(response: any[], model: any, term: string) {
    const selectedEventTeams = this.selectedTeams.find((selectedEvent) => selectedEvent.eventId === model.eventId);

    if (selectedEventTeams && !selectedEventTeams.teams.length) {
      this.gameAutocompleteOptions.noItemsText = 'Team not selected for this event';
    }

    return response
      .filter(
        (game) =>
          game.event.id === model.eventId &&
          // if team in selected teams
          game.teams.some((gameTeam) =>
            selectedEventTeams.teams.some((selectedTeam) => selectedTeam.id === gameTeam.id)
          ) &&
          (game.teams[0].name.toLowerCase().indexOf(term.toLowerCase()) >= 0 ||
            game.teams[1].name.toLowerCase().indexOf(term.toLowerCase()) >= 0)
      )
      .map((item) => {
        return { id: item.id, name: item.teams.map((team) => team.name).join(' vs ') };
      });
  }

  formatClipsResponse(response: any[], model: any, term: string) {
    return response.map((item) => {
      return { id: item.id, name: item.name };
    });
  }

  changeEvent(event) {
    this.dropdownGames = [];
    this.playInfoForm.patchValue({
      gameId: null,
    });
    if (event?.id) {
      this.setGamesDropdown();
    }
  }

  changeGame(game) {
    if (game && game.id) {
      const fullGame = game.assets.find((asset) => asset.type === Constants.ASSET_TYPE.FULL_GAME_VIDEO);
      const gameData = {
        gameName: fullGame.name,
        gameId: game.id,
        gameDate: game.date,
        eventName: game.event ? game.event.name : '',
        thumbnail: '',
      };
      if (!this.selectClips) {
        this.assetsService.getVideoThumb(fullGame).subscribe((thumbnail) => {
          gameData.thumbnail = thumbnail;
          this.playInfoForm.patchValue(gameData);
        });
      } else {
        this.playInfoForm.patchValue(gameData);
      }
    }
  }

  onClipChangedDropdown(clip) {
    if (clip) {
      const clipId = clip.id;

      if (!clipId) {
        this.playInfoForm.get('clipName').reset();
        return;
      }

      const assetData = clip;
      const clipData = {
        clip,
        clipName: assetData.name,
        clipEvent: assetData.event ? assetData.event.name : null,
        clipGame: assetData.game ? assetData.game.name : null,
        eventId: assetData.event ? assetData.event.id : null,
        gameId: assetData.game ? assetData.game.id : null,
        thumbnail: '',
      };
      this.assetsService.getVideoThumb(assetData).subscribe((thumbnail) => {
        clipData.thumbnail = thumbnail;
      });
      this.playInfoForm.patchValue(clipData);
      this.playInfoForm.updateValueAndValidity({ emitEvent: true });
    }
  }

  onClipOptionChange(option) {
    if (option && option.value === 1) {
      this.uploadClip = false;
    } else {
      this.uploadClip = true;
    }
    this.uploadClipChange.emit(this.uploadClip);
  }

  private loadEvents() {
    let initialEventList = [];
    const event = { id: this.playInfoForm.value.eventId, name: this.playInfoForm.value.eventName };
    if (event && event.id && event.name) {
      initialEventList = [{ id: event.id, name: event.name }];
    }

    this.eventsList$ = concat(
      of(initialEventList), // default items
      this.eventInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.eventLoading = true)),
        switchMap((term) =>
          this.eventsService.searchEvents(term, 100).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.eventLoading = false)),
            map(this.formatEventsSearchResponse)
          )
        )
      )
    );
  }

  private setGamesDropdown() {
    if (!this.playInfoForm.value.eventId) {
      return;
    }
    this.dropdownGames = [];
    const event = { id: this.playInfoForm.value.eventId };
    const TAKE = 200;
    let skip = 0;
    const gamesSubject$ = new BehaviorSubject({ take: TAKE, skip });
    let totalNumberOfGames = 0;
    gamesSubject$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.gamesLoading = true)),
        switchMap((params) => {
          return this.eventsService.getEventGames(event, params.take, params.skip).pipe(
            map((games) => {
              totalNumberOfGames = games.length;
              return games
                .filter((game) => {
                  const teams = this.selectedTeams.find((t) => t.eventId === game.event.id);
                  return (
                    game.event.id === event.id &&
                    // if team in selected teams
                    (!teams ||
                      game.teams.some((gameTeam) => teams.teams.some((selectedTeam) => selectedTeam === gameTeam.id)))
                  );
                })
                .map((item) => {
                  return { ...item, value: item.id, name: item.teams.map((team) => team.name).join(' vs ') };
                })
                .map((game, index, dGames) => {
                  return {
                    ...game,
                    showTime: dGames.filter((dg) => dg.name === game.name)?.length > 1,
                  };
                })
                .sort(this.utilsService.sortStringByProp);
            })
          );
        }),
        tap((games) => {
          if (totalNumberOfGames === TAKE) {
            // setTimeout(() => {
            skip += TAKE;
            gamesSubject$.next({ take: TAKE, skip });
            // }, 500);
          } else {
            this.gamesLoading = false;
          }
        })
      )
      .subscribe((games) => {
        this.dropdownGames = [...this.dropdownGames, ...games];
      });
  }

  private formatEventsSearchResponse(response) {
    if (!response || !response.body || !response.body.hits || !response.body.hits.hits) {
      return [];
    }
    return response.body.hits.hits.map((eventResponse) => ({
      id: eventResponse._source.id,
      name: eventResponse._source.name,
      date: eventResponse._source.startdate,
    }));
  }
}
